import * as yup from 'yup';
import moment from 'moment';
import { FIELD_LIMITS } from 'utils/character-count-utils';

export const MULTI_DAY_EVENT_START_TIME = '00:00:00';
export const MULTI_DAY_EVENT_END_TIME = '23:59:00';

const isMultiDayEvent = (startDate?: string, endDate?: string) => {
  if (!startDate || !endDate) {
    return false;
  }

  const start = moment(startDate, 'YYYY-MM-DD', true);
  const end = moment(endDate, 'YYYY-MM-DD', true);

  if (!start.isValid() || !end.isValid()) {
    return false;
  }

  return !start.isSame(end, 'day');
};

const eventLocationValidation = yup.object().shape({
  startDate: yup.string().trim().required('Start date is required'),
  endDate: yup.string().trim().required('End date is required')
    .test(
      'end-date-after-start-date',
      'End date must be the same as or after start date',
      function validateEndDate(value) {
        const { startDate } = this.parent;
        if (!value || !startDate) {
          return true;
        }

        const start = moment(startDate, 'YYYY-MM-DD', true);
        const end = moment(value, 'YYYY-MM-DD', true);

        if (!start.isValid() || !end.isValid()) {
          return true;
        }

        return !end.isBefore(start, 'day');
      },
    ),
  from: yup.string().trim()
    .test(
      'start-time-required',
      'Start time is required',
      function validateStartTimeRequired(value) {
        const { startDate, endDate } = this.parent;
        if (!startDate || !endDate) {
          return true;
        }

        if (isMultiDayEvent(startDate, endDate)) {
          return true;
        }

        return !!value;
      },
    )
    .test(
      'multi-day-start-time',
      'For multi-day events, start time is fixed to 00:00',
      function validateMultiDayStartTime(value) {
        const { startDate, endDate } = this.parent;
        if (!isMultiDayEvent(startDate, endDate)) {
          return true;
        }

        return value === MULTI_DAY_EVENT_START_TIME;
      },
    )
    .test('not-past-time', 'Start time cannot be in the past', function validateStartTimeNotPast(value) {
      const { startDate, endDate } = this.parent;
      if (!value || !startDate) {
        return true;
      }

      if (isMultiDayEvent(startDate, endDate)) {
        return true;
      }

      const selectedDate = moment(startDate, 'YYYY-MM-DD', true);
      const today = moment().startOf('day');
      if (!selectedDate.isValid()) {
        return true;
      }
      if (selectedDate.isSame(today)) {
        const startTime = moment(`${startDate} ${value}`, ['YYYY-MM-DD HH:mm:ss', 'YYYY-MM-DD HH:mm', 'YYYY-MM-DD hh:mm A'], true);
        if (!startTime.isValid()) {
          return true;
        }
        return startTime.isAfter(moment());
      }
      return true;
    }),
  to: yup.string().trim()
    .test(
      'end-time-required',
      'End time is required',
      function validateEndTimeRequired(value) {
        const { startDate, endDate } = this.parent;
        if (!startDate || !endDate) {
          return true;
        }

        if (isMultiDayEvent(startDate, endDate)) {
          return true;
        }

        return !!value;
      },
    )
    .test(
      'multi-day-end-time',
      'For multi-day events, end time is fixed to 23:59',
      function validateMultiDayEndTime(value) {
        const { startDate, endDate } = this.parent;
        if (!isMultiDayEvent(startDate, endDate)) {
          return true;
        }

        return value === MULTI_DAY_EVENT_END_TIME;
      },
    )
    .test('end-after-start', 'End time must be after start time', function validateEndTime(value) {
      const {
        startDate, endDate, from,
      } = this.parent;
      if (!value || !from || !startDate || !endDate) {
        return true;
      }

      if (isMultiDayEvent(startDate, endDate)) {
        return true;
      }

      const start = moment(`${startDate} ${from}`, ['YYYY-MM-DD HH:mm:ss', 'YYYY-MM-DD HH:mm', 'YYYY-MM-DD hh:mm A'], true);
      const end = moment(`${endDate} ${value}`, ['YYYY-MM-DD HH:mm:ss', 'YYYY-MM-DD HH:mm', 'YYYY-MM-DD hh:mm A'], true);
      if (!start.isValid() || !end.isValid()) {
        return true;
      }
      return end.isAfter(start);
    })
    .test('not-past-time', 'End time cannot be in the past', function validateEndTimeNotPast(value) {
      const { startDate, endDate } = this.parent;
      if (!value || !endDate) {
        return true;
      }

      if (isMultiDayEvent(startDate, endDate)) {
        return true;
      }

      const selectedDate = moment(endDate, 'YYYY-MM-DD', true);
      const today = moment().startOf('day');
      if (!selectedDate.isValid()) {
        return true;
      }
      if (selectedDate.isSame(today)) {
        const endTime = moment(`${endDate} ${value}`, ['YYYY-MM-DD HH:mm:ss', 'YYYY-MM-DD HH:mm', 'YYYY-MM-DD hh:mm A'], true);
        if (!endTime.isValid()) {
          return true;
        }
        return endTime.isAfter(moment());
      }
      return true;
    }),
  address: yup.string().trim().required('Address is required').max(FIELD_LIMITS.ADDRESS.maxLength, `Address must be at most ${FIELD_LIMITS.ADDRESS.maxLength} characters`),
  description: yup.string().trim()
    .test(
      'length-limit',
      `Location description must have upto ${FIELD_LIMITS.SESSION_DESC.maxLength} characters`,
      (value) => !value || value?.replace(/(<([^>]+)>)/ig, '').replace(/[\r\n]+/gm, '').length <= FIELD_LIMITS.SESSION_DESC.maxLength,
    ),
});

const eventLocationsValidation = yup.object().shape({
  locations: yup.array().of(eventLocationValidation)
    .min(1, 'At least one location is required')
    .max(7, 'You can add up to 7 locations'),
});

export const eventLocationsFieldConfigs = {
  address: FIELD_LIMITS.ADDRESS,
  description: FIELD_LIMITS.SESSION_DESC,
};

export default eventLocationsValidation;
